import { Box, Chip } from '@mui/material';
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  SelectInput,
  TextField,
  TextInput,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { formatBalance } from './formatBalance';

/**
 * Users list — Phase 1 read-only surface, the same proven pattern as the Stores
 * list (region-driven <List>/<Datagrid>, RequireAccess gating, custom nav).
 *
 * Users belong to a single market, so the list shows users of the ACTIVE region
 * via the provider's existing `region` filter (the one marketsForScope mapping).
 * Flip the switcher and the rows change to that market; a single-region user
 * only ever sees theirs. No new region mechanism.
 *
 * `balance` is DERIVED on read by the provider (confirmed cashback minus
 * settled / in-flight cashouts), never stored — and materialised BEFORE sort,
 * which is why sorting by balance works here.
 *
 * SWAP-TIME CONSIDERATION (not a problem to solve now): the real admin API must
 * compute this balance efficiently for LIST queries — a precomputed/aggregated
 * column, not a per-row N+1 — since list pages sort and page over it. It is
 * cheap here only because the mock is fully in-memory.
 */

const TierChip = () => {
  const record = useRecordContext();
  const plus = record?.tier === 'plus';
  return (
    <Chip
      size="small"
      label={plus ? 'Plus' : 'Free'}
      color={plus ? 'primary' : 'default'}
      variant={plus ? 'filled' : 'outlined'}
    />
  );
};

/**
 * Internal "Legacy member" marker for users migrated from the old platform
 * (isLegacy, PR #28). Ops-only display — NOT the consumer-facing acknowledgement
 * (perks/recognition), which stays a deferred product decision (D-legacy-users).
 * Renders nothing for non-legacy users.
 */
export const LegacyChip = () => {
  const record = useRecordContext();
  if (!record?.isLegacy) return null;
  return (
    <Chip size="small" label="Legacy member" color="info" variant="outlined" />
  );
};

// Region is driven by the switcher, NOT a column filter. `q` is a
// case-insensitive substring search (matches name + email).
const userFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <SelectInput
    key="tier"
    source="tier"
    label="Tier"
    choices={[
      { id: 'free', name: 'Free' },
      { id: 'plus', name: 'Plus' },
    ]}
  />,
  <SelectInput
    key="signupMethod"
    source="signupMethod"
    label="Signup method"
    choices={[
      { id: 'social', name: 'Social' },
      { id: 'email', name: 'Email' },
    ]}
  />,
];

/** The list itself. Exported for testing without the access wrapper. */
export const UserListView = () => {
  const { activeRegion } = useRegion();
  return (
    <List
      resource="users"
      title="Users"
      filter={{ region: activeRegion }}
      filters={userFilters}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      {/* Clicking a row opens the user detail (keyed by user id). No bulk
          actions (read-only list). */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        {/* Name with an inline legacy marker (kept in the existing column — no
            extra column, so the table stays uncluttered). */}
        <FunctionField
          label="Name"
          sortBy="name"
          render={(record: RaRecord) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{record.name as string}</span>
              <LegacyChip />
            </Box>
          )}
        />
        <TextField source="email" label="Email" />
        <FunctionField label="Tier" sortBy="tier" render={() => <TierChip />} />
        <FunctionField
          label="Balance"
          sortBy="balance"
          render={(record: RaRecord) =>
            formatBalance(record.balance, record.market)
          }
        />
        <DateField source="createdAt" label="Signup date" />
        <TextField source="signupMethod" label="Signup method" />
      </Datagrid>
    </List>
  );
};

/**
 * Gated entry point registered as the `users` Resource's list. Read-only, so
 * `viewer` (read everywhere) sees it alongside `super_admin`; editor/support/
 * fraud lack `users:read` and never see the route or the menu item.
 */
export const UserList = () => (
  <RequireAccess capability="users:read">
    <UserListView />
  </RequireAccess>
);
